/**
 * NdfComptaPanel — écran 4 du mockup : file d'attente & remboursement.
 * Onglet Comptabilité → « Notes de frais ». Reproduit le mockup à l'identique.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import type { NdfNote } from '@roronoa-collectivites/shared-types';

const eur = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;
const COMPTA = 'oklch(var(--c-compta))';

type ComptaNote = NdfNote & { valideN1Nom: string | null; n1Auto: boolean };

const SVC_DOT: Record<string, string> = {
  service_technique: 'oklch(var(--c-tech))', scolaire_cantine: 'oklch(var(--c-scolaire))',
  grc: 'oklch(var(--c-grc))', petite_enfance: 'oklch(var(--c-petite_enfance))',
  urbanisme: 'oklch(var(--c-urba))', comptable: 'oklch(var(--c-compta))',
};
const SVC_LABEL: Record<string, string> = {
  service_technique: 'Technique', scolaire_cantine: 'Scolaire', grc: 'Communication',
  petite_enfance: 'Petite enfance', urbanisme: 'Urbanisme', comptable: 'Comptabilité',
};

export function NdfComptaPanel() {
  const [tab, setTab] = useState<'attente' | 'arembourser' | 'remboursees' | 'all'>('attente');
  const [notes, setNotes] = useState<ComptaNote[]>([]);
  const [stats, setStats] = useState<{ aValider: number; aRembourser: number; ceMois: number; annee: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [detail, setDetail] = useState<NdfNote | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [modes, setModes] = useState<Record<string, string>>({});

  async function charger() {
    try {
      const [n, s] = await Promise.all([api.notesFrais.notes.compta(tab), api.notesFrais.notes.comptaStats()]);
      setNotes(n); setStats(s);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, [tab]);

  async function valider(id: string) {
    setError(null); setMsg(null);
    try { await api.notesFrais.notes.validerCompta(id); setMsg('Note validée.'); setDetail(null); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function rembourser(id: string) {
    try { await api.notesFrais.notes.rembourser(id, modes[id] ?? 'virement'); setMsg('Note marquée remboursée.'); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function confirmerRefus() {
    if (!refus || !motif.trim()) return;
    try { await api.notesFrais.notes.refuser(refus, motif.trim()); setRefus(null); setMotif(''); setDetail(null); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function ouvrirDetail(id: string) {
    try { setDetail(await api.notesFrais.notes.get(id)); } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Onglets internes (mockup écran 4) */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {([['attente', `À valider${stats ? ` (${stats.aValider})` : ''}`], ['arembourser', `À rembourser${stats ? ` (${stats.aRembourser})` : ''}`], ['remboursees', 'Remboursées'], ['all', 'Toutes']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`rounded-r-pill border px-3 py-1.5 text-xs font-medium ${tab === k ? 'text-white' : 'border-border bg-transparent text-ink-muted hover:text-ink'}`}
            style={tab === k ? { background: COMPTA, borderColor: 'transparent' } : undefined}>{label}</button>
        ))}
      </div>

      {stats && (
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))' }}>
          <StatBox label="À valider" value={String(stats.aValider)} color={COMPTA} />
          <StatBox label="À rembourser" value={String(stats.aRembourser)} tone="warn" />
          <StatBox label="Ce mois" value={eur(stats.ceMois)} />
          <StatBox label="Remboursé (année)" value={eur(stats.annee)} tone="ok" />
        </div>
      )}

      {tab === 'attente' && (
        <Card variant="glass" padded={false}>
          <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-ink">Validées par le manager — en attente comptabilité</h3></div>
          <Tableau notes={notes} cols="attente" onDetail={ouvrirDetail} onValider={valider} onRefuser={(id) => { setRefus(id); setMotif(''); }} />
        </Card>
      )}

      {tab === 'arembourser' && (
        <Card variant="glass" padded={false}>
          <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-ink">Validées comptabilité — à rembourser</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Déclarant</th><th className="px-4 py-2.5 font-medium">Note</th>
                <th className="px-4 py-2.5 text-right font-medium">Montant</th><th className="px-4 py-2.5 font-medium">Mode</th><th className="px-4 py-2.5 text-right font-medium">Action</th>
              </tr></thead>
              <tbody>
                {notes.map((n) => (
                  <tr key={n.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 text-ink">{n.declarantNom ?? n.tiersNom ?? '—'}</td>
                    <td className="px-4 py-2.5 text-ink">{n.titre}{n.deplacementObjet && <span className="ml-2 rounded-r-pill bg-rh/10 px-2 py-0.5 text-2xs text-ink-muted">Déplacement : {n.deplacementObjet}</span>}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink">{eur(n.montantRetenu)}</td>
                    <td className="px-4 py-2.5">
                      <select value={modes[n.id] ?? 'virement'} onChange={(e) => setModes((m) => ({ ...m, [n.id]: e.target.value }))} className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
                        <option value="virement">Virement</option><option value="paie">Via la paie</option><option value="autre">Autre</option>
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-right"><Button variant="secondary" size="sm" style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => void rembourser(n.id)}>Marquer remboursée</Button></td>
                  </tr>
                ))}
                {notes.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-ink-subtle">Aucune note à rembourser.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2.5 text-xs text-ink-muted">Le versement effectif est réalisé par la trésorerie / la paie. VPC trace la décision, le mode et la référence, et alimente le journal comptable (compte M57 du type de frais).</div>
        </Card>
      )}

      {(tab === 'remboursees' || tab === 'all') && (
        <Card variant="glass" padded={false}>
          <Tableau notes={notes} cols="simple" onDetail={ouvrirDetail} />
        </Card>
      )}

      {/* Détail */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={detail ? `Détail — ${detail.titre}` : ''}
        footer={detail && detail.statut === 'attente_compta' && <><Button variant="danger" onClick={() => { setRefus(detail.id); setMotif(''); }}>Refuser…</Button><Button variant="secondary" style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => void valider(detail.id)}>Valider</Button></>}>
        {detail && <DetailLignes note={detail} />}
      </Dialog>

      {/* Refus */}
      <Dialog open={!!refus} onClose={() => setRefus(null)} title="Refuser la note de frais"
        footer={<><Button variant="ghost" onClick={() => setRefus(null)}>Annuler</Button><Button variant="danger" disabled={!motif.trim()} onClick={() => void confirmerRefus()}>Confirmer le refus</Button></>}>
        <div><div className="mb-1 text-xs text-ink-subtle">Motif (communiqué au déclarant)</div>
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} className="w-full rounded-r-md border border-border bg-surface/60 px-2.5 py-2 text-sm text-ink" /></div>
      </Dialog>
    </div>
  );
}

function Tableau({ notes, cols, onDetail, onValider, onRefuser }: {
  notes: ComptaNote[]; cols: 'attente' | 'simple';
  onDetail: (id: string) => void; onValider?: (id: string) => void; onRefuser?: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
            <th className="px-4 py-2.5 font-medium">Déclarant</th><th className="px-4 py-2.5 font-medium">Service</th>
            <th className="px-4 py-2.5 font-medium">Note</th><th className="px-4 py-2.5 text-right font-medium">Montant</th>
            {cols === 'attente' ? <><th className="px-4 py-2.5 font-medium">Validée N1</th><th className="px-4 py-2.5 text-right font-medium">Décision</th></> : <><th className="px-4 py-2.5 font-medium">Statut</th><th className="px-4 py-2.5 text-right font-medium"></th></>}
          </tr>
        </thead>
        <tbody>
          {notes.map((n) => (
            <tr key={n.id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-2.5 text-ink">{n.declarantNom ?? n.tiersNom ?? '—'}</td>
              <td className="px-4 py-2.5">{n.tiersId ? <span className="inline-flex items-center gap-1.5 text-xs"><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'oklch(var(--c-rh))' }} />Tiers</span> : n.service ? <span className="inline-flex items-center gap-1.5 text-xs"><span className="h-1.5 w-1.5 rounded-full" style={{ background: SVC_DOT[n.service] ?? '#888' }} />{SVC_LABEL[n.service] ?? n.service}</span> : <span className="inline-flex items-center gap-1.5 text-xs"><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'oklch(var(--c-rh))' }} />Élu</span>}</td>
              <td className="px-4 py-2.5 text-ink">{n.titre}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink">{eur(n.montantRetenu)}</td>
              {cols === 'attente' ? (
                <>
                  <td className="px-4 py-2.5 text-ink-muted">{n.n1Auto ? <Badge variant="neutral">{n.tiersId ? 'Tiers (compta)' : 'N1 auto (élu)'}</Badge> : (n.valideN1Nom ?? '—')}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="ghost" size="sm" onClick={() => onDetail(n.id)}>Détail</Button>{' '}
                    <Button variant="secondary" size="sm" style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => onValider?.(n.id)}>Valider</Button>{' '}
                    <button type="button" onClick={() => onRefuser?.(n.id)} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2.5"><Badge variant={n.statut === 'remboursee' ? 'success' : n.statut === 'refusee' ? 'urgent' : 'info'}>{n.statut === 'remboursee' ? 'Remboursé' : n.statut === 'refusee' ? 'Refusée' : n.statut === 'validee' ? 'À rembourser' : 'En attente'}</Badge></td>
                  <td className="px-4 py-2.5 text-right"><Button variant="ghost" size="sm" onClick={() => onDetail(n.id)}>Détail</Button></td>
                </>
              )}
            </tr>
          ))}
          {notes.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-ink-subtle">Aucune note.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DetailLignes({ note }: { note: NdfNote }) {
  return (
    <div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-ink-subtle">
          <th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Libellé</th>
          <th className="py-2 pr-3 font-medium">Détail</th><th className="py-2 text-right font-medium">Montant</th>
        </tr></thead>
        <tbody>
          {(note.lignes ?? []).map((l) => (
            <tr key={l.id} className="border-b border-border/60 last:border-0">
              <td className="py-2 pr-3"><Badge variant={l.distanceKm != null ? 'rh' : 'neutral'}>{l.typeLibelle ?? '—'}</Badge></td>
              <td className="py-2 pr-3 text-ink">{l.libelle ?? '—'}</td>
              <td className="py-2 pr-3 text-ink-subtle">{l.distanceKm != null ? `${l.distanceKm} km · ${l.puissanceFiscale} CV` : l.depassePlafond ? `plafond dépassé (retenu ${eur(l.montantRetenu)})` : '—'}</td>
              <td className="py-2 text-right tabular-nums text-ink">{eur(l.montantTtc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <span className="text-xs text-ink-subtle">Total retenu</span>
        <span className="text-lg font-bold text-ink">{eur(note.montantRetenu)}</span>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone, color }: { label: string; value: string; tone?: 'ok' | 'warn'; color?: string }) {
  const c = tone === 'ok' ? 'text-success' : tone === 'warn' ? 'text-warn' : '';
  return <div className="rounded-r-lg border border-border bg-surface px-3.5 py-3"><div className="text-[11px] text-ink-subtle">{label}</div><div className={`mt-0.5 text-2xl font-bold ${c || 'text-ink'}`} style={color && !tone ? { color } : undefined}>{value}</div></div>;
}
